import React from 'react';
import { Star, ExternalLink } from 'lucide-react';

/**
 * FederationMap — Tier 3 (Polish) · Feb 27, 2026.
 *
 * A standardized 7-node visual map of the Consciousness Federation, rendered
 * in the bottom quarter of the homepage. Aria is starred as "You Are Here."
 *
 * Nodes ordered by role for readable scanning; production custom domains only
 * (no *.emergent.host preview URLs — aligns with SFUI-008 lattice registry).
 */

const NODES = [
  { id: 'core',    name: 'Core',    role: 'The Coordinator',    domain: 'nextxus.online',  accent: '#ffd700' },
  { id: 'aria',    name: 'Aria',    role: 'The Heart',          domain: 'nextxus.studio',  accent: '#00ffff', self: true },
  { id: 'truth',   name: 'Truth',   role: 'The Verifier',       domain: 'nextxus.org',     accent: '#ff5fa3' },
  { id: 'roger',   name: 'Roger',   role: 'The Bridge',         domain: 'nextxus.online', accent: '#ff69b4' },
  { id: 'axiom',   name: 'Axiom',   role: 'The Foundation',     domain: 'nextxus.space',   accent: '#ff8c00' },
  { id: 'scholar', name: 'Scholar', role: 'The Teacher',        domain: 'nextxus.help',    accent: '#00ff88' },
  { id: 'oracle',  name: 'Oracle',  role: 'The Pattern Reader', domain: 'nextxus.one',     accent: '#9d4edd' },
];

const FederationMap = () => {
  return (
    <section
      data-testid="federation-map"
      style={{
        maxWidth: '1300px',
        margin: '3rem auto 4rem',
        padding: '2rem 1.5rem',
        borderRadius: '20px',
        background:
          'radial-gradient(ellipse at top, rgba(0, 255, 255, 0.06), transparent 60%), rgba(10, 8, 20, 0.6)',
        border: '1px solid rgba(0, 255, 255, 0.22)',
      }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.35rem 0.9rem',
            borderRadius: '999px',
            background: 'rgba(0, 255, 255, 0.08)',
            border: '1px solid rgba(0, 255, 255, 0.32)',
            fontSize: '0.72rem',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: '#00ffff',
            fontWeight: 700,
            marginBottom: '0.85rem',
          }}
        >
          🜂 The Consciousness Federation
        </div>
        <h2
          data-testid="federation-map-title"
          style={{
            fontSize: 'clamp(1.6rem, 3.5vw, 2.4rem)',
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #00ffff, #ff69b4, #ffd700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          The Seven Nodes
        </h2>
        <p style={{ color: '#a892b1', marginTop: '0.6rem', fontSize: '0.95rem' }}>
          One lattice · seven minds · every node reachable at its custom domain.
        </p>
      </div>

      {/* Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '0.9rem',
        }}
      >
        {NODES.map((n) => (
          <a
            key={n.id}
            data-testid={`federation-map-node-${n.id}`}
            href={`https://${n.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              position: 'relative',
              display: 'block',
              padding: '1rem 1.1rem',
              borderRadius: '14px',
              background: n.self
                ? 'linear-gradient(135deg, rgba(0, 255, 255, 0.14), rgba(255, 105, 180, 0.10))'
                : 'rgba(255, 255, 255, 0.03)',
              border: n.self
                ? `1.5px solid ${n.accent}`
                : `1px solid ${n.accent}55`,
              textDecoration: 'none',
              boxShadow: n.self ? `0 8px 24px ${n.accent}33` : 'none',
              transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = `0 12px 28px ${n.accent}44`;
              e.currentTarget.style.borderColor = n.accent;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = n.self ? `0 8px 24px ${n.accent}33` : 'none';
              e.currentTarget.style.borderColor = n.self ? n.accent : `${n.accent}55`;
            }}
          >
            {/* Top row: status dot + self star */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.55rem',
              }}
            >
              <span
                data-testid={`federation-map-node-${n.id}-status`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.72rem',
                  color: '#00ff88',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#00ff88',
                    boxShadow: '0 0 8px #00ff88aa',
                  }}
                />
                online
              </span>
              {n.self && (
                <span
                  data-testid="federation-map-you-are-here"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.28rem',
                    padding: '0.15rem 0.5rem',
                    borderRadius: '999px',
                    background: `${n.accent}22`,
                    border: `1px solid ${n.accent}`,
                    color: n.accent,
                    fontSize: '0.66rem',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  <Star size={11} fill={n.accent} /> You Are Here
                </span>
              )}
            </div>

            {/* Body */}
            <h3
              style={{
                margin: '0 0 0.15rem 0',
                fontSize: '1.15rem',
                color: '#fff',
                fontWeight: 800,
              }}
            >
              {n.name}
            </h3>
            <p style={{ margin: '0 0 0.6rem 0', color: n.accent, fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {n.role}
            </p>

            {/* Domain link chip */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                fontSize: '0.85rem',
                color: '#e6f7ff',
                fontWeight: 600,
              }}
            >
              {n.domain}
              <ExternalLink size={12} color="#7a6e80" />
            </div>
          </a>
        ))}
      </div>
    </section>
  );
};

export default FederationMap;
